import { useEffect, useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Tooltip } from '@progress/kendo-react-tooltip';

import FleetAircraftAccordion from '@shared/components/FleetAircraftAccordion';
import { FleetAircraft } from '@shared/components/FleetAircraftList';
import { OperatorDocument } from '@shared/components/OperatorOverview';
import documentIcon from '@shared/components/OperatorOverview/file-icon.svg';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import classes from './AircraftOverview.module.scss';

export interface AircraftPhoto {
  key: string;
  url: string;
  name?: string | null;
  isPdf?: boolean;
}

export interface AircraftAmenity {
  id: number;
  name: string;
}

interface AircraftOverviewProps {
  aircraftName: string;
  tail?: string;
  operatorName?: string;
  type?: string;
  /** Already resolved: a fleet shows the spread across its members, an aircraft its own. */
  paxDisplay: string;
  photos?: AircraftPhoto[];
  base?: string;
  yearOfMake?: string | number;
  yearOfRefurb?: string | number;
  argusRating?: string;
  wyvernRating?: string;
  approved: boolean;
  createdDate?: string;
  updatedDate?: string;
  notes?: string;
  amenities?: AircraftAmenity[];
  /** The fleet this aircraft belongs to. */
  fleetName?: string;
  /** Set when this aircraft is itself a fleet. */
  assignedAircraft?: FleetAircraft[];
  isFleet?: boolean;
  documents?: OperatorDocument[];
  documentsDisclaimer?: string;
  onAircraftClick?: (aircraft: FleetAircraft) => void;
}

const AircraftOverview = ({
  aircraftName,
  tail,
  operatorName,
  type,
  paxDisplay,
  photos = [],
  base,
  yearOfMake,
  yearOfRefurb,
  argusRating,
  wyvernRating,
  approved,
  createdDate,
  updatedDate,
  notes,
  amenities = [],
  fleetName,
  assignedAircraft = [],
  isFleet = false,
  documents = [],
  documentsDisclaimer,
  onAircraftClick,
}: AircraftOverviewProps): JSX.Element => {
  const [ activeIndex, setActiveIndex ] = useState(0);
  const [ preview, setPreview ] = useState<AircraftPhoto | null>(null);

  const photoKeys = photos.map((photo) => photo.key).join('|');
  useEffect(() => {
    setActiveIndex(0);
  }, [ photoKeys ]);

  return (
    <div className={classes.aircraftOverview}>
      {preview && (
        <Dialog title="Preview" onClose={() => setPreview(null)} className={classes.aircraftOverviewPreview}>
          <div className="frame">
            {preview.isPdf
              ? <embed height="100%" width="100%" src={preview.url} />
              : <img src={preview.url} alt={preview.name || 'Aircraft'} />}
          </div>
        </Dialog>
      )}

      <div className="masthead">
        {photos.length > 0 && (
          <div className="shots">
            <div className="viewport">
              <div className="track" style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
                {photos.map((photo, index) => {
                  // The track clips every slide but the selected one, so only that slide
                  // stays in the tab order.
                  const isActive = index === activeIndex;
                  return (
                    <button
                      key={photo.key}
                      type="button"
                      className="shot"
                      tabIndex={isActive ? 0 : -1}
                      aria-hidden={isActive ? undefined : true}
                      onClick={() => setPreview(photo)}
                    >
                      <img src={photo.url} alt={photo.name || `Aircraft ${index + 1}`} />
                    </button>
                  );
                })}
              </div>
            </div>
            {photos.length > 1 && (
              <div className="dots">
                {photos.map((photo, index) => (
                  <button
                    key={`dot-${photo.key}`}
                    type="button"
                    aria-label={`Show image ${index + 1}`}
                    className={index === activeIndex ? 'current' : undefined}
                    onClick={() => setActiveIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <Tooltip anchorElement="target" position="bottom" openDelay={100}>
          <div className="identity">
            <h3 title={aircraftName}>{aircraftName}</h3>
            {tail && <span className="reg">{tail}</span>}
            {operatorName && (
              <p className="operated">
                Operated by <span title={operatorName}>{operatorName}</span>
              </p>
            )}
            <p className="kind">{type ? `${type} Jet` : ''}</p>
            <p className="pax"><span className="count">{paxDisplay}</span> Max Passengers</p>
          </div>
        </Tooltip>
      </div>

      <div className="stats">
        {base && <div className="stat"><span className="caption">Base</span><span className="readout">{base}</span></div>}
        {yearOfMake && <div className="stat"><span className="caption">Year of Make</span><span className="readout">{yearOfMake}</span></div>}
        {yearOfRefurb && <div className="stat"><span className="caption">Year of Refurbish</span><span className="readout">{yearOfRefurb}</span></div>}
        {argusRating && <div className="stat"><span className="caption">Argus Rating</span><span className="readout">{argusRating}</span></div>}
        {wyvernRating && <div className="stat"><span className="caption">Wyvern Rating</span><span className="readout">{wyvernRating}</span></div>}
        <div className="stat">
          <span className="caption">Status</span>
          <span className="readout">{approved ? 'Approved' : 'Do Not Use'}</span>
        </div>
        {createdDate && <div className="stat"><span className="caption">Created Date</span><span className="readout">{createdDate}</span></div>}
        {updatedDate && <div className="stat"><span className="caption">Updated Date</span><span className="readout">{updatedDate}</span></div>}
      </div>

      {notes && (
        <div className="remark">
          <span className="caption">Aircraft Notes</span>
          <p className="readout">{notes}</p>
        </div>
      )}

      {amenities.length > 0 && (
        <div className="remark">
          <span className="caption">Amenities</span>
          <div className="pills">
            {amenities.map((amenity) => <span className="pill" key={amenity.id}>{amenity.name || ' - '}</span>)}
          </div>
        </div>
      )}

      {isFleet ? (
        <div className="fleets">
          <FleetAircraftAccordion
            panels={[ { key: 'assigned-aircraft', title: 'Assigned Aircraft', aircraft: assignedAircraft, expanded: true } ]}
            onAircraftClick={onAircraftClick}
          />
        </div>
      ) : fleetName ? (
        <div className="remark">
          <span className="caption">Fleet</span>
          <div className="pills"><span className="pill">{fleetName}</span></div>
        </div>
      ) : null}

      {documents.length > 0 && (
        <div className="docs">
          <div className="rule" />
          <PanelBar>
            <PanelBarItem expanded title="Documents">
              <div className="content">
                {documentsDisclaimer && <p className="disclaimer">{documentsDisclaimer}</p>}
                {documents.map((file) => (
                  <a className="doc" key={file.key} href={file.url} download={file.name} target="_blank" rel="noreferrer">
                    <img src={file.thumbnailUrl || documentIcon} alt="" />
                    <span>{file.name}</span>
                  </a>
                ))}
              </div>
            </PanelBarItem>
          </PanelBar>
        </div>
      )}
    </div>
  );
};

export default AircraftOverview;
